import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Locales matching SUPPORTED_LANGUAGES in useLanguage.ts
import 'dayjs/locale/de';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/it';
import 'dayjs/locale/ja';
import 'dayjs/locale/nl';
import 'dayjs/locale/pt';
import 'dayjs/locale/sv';

dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

/** Set dayjs's global locale — called from i18n.ts on init + languageChanged */
export function setDayjsLocale(lng: string): void {
  dayjs.locale(lng);
}

export type DateInput = string | number | Date | null | undefined;

interface FormatOptions {
  fallback?: string;
  tz?: string;
  locale?: string;
}

/** Safe dayjs format — returns fallback on null/undefined/invalid */
function safeDayjsFormat(value: DateInput, formatStr: string, opts: FormatOptions = {}): string {
  const { fallback = '', tz, locale } = opts;
  if (value == null || value === '') return fallback;
  try {
    let d = tz ? dayjs(value).tz(tz) : dayjs(value);
    if (!d.isValid()) return fallback;
    if (locale) d = d.locale(locale);
    return d.format(formatStr);
  } catch {
    return fallback;
  }
}

/**
 * Convert a "display-time-as-UTC" value from the backend back to local time.
 * The backend stores the user's intended display time encoded as UTC (e.g. 3:00 PM local
 * is stored as 2026-03-10T15:00:00Z). We need to reverse this to get the actual local Date.
 */
export function fromDisplayUtc(isoString: string): Date {
  const d = new Date(isoString);
  return new Date(d.getTime() + d.getTimezoneOffset() * 60000);
}

/** "4/3/26, 10:33 AM" — compact for grid display */
export function formatDateShort(value: DateInput, fallback = ''): string {
  return safeDayjsFormat(value, 'M/D/YY, h:mm A', { fallback });
}

/** "Apr 03, 2026 at 10:33 AM" */
export function formatDateTimeLong(value: DateInput, fallback = ''): string {
  return safeDayjsFormat(value, 'MMM DD, YYYY [at] h:mm A', { fallback });
}

/** "April 3, 2026" — long date, no time */
export function formatDateLong(value: DateInput, fallback = ''): string {
  return safeDayjsFormat(value, 'MMMM D, YYYY', { fallback });
}

/** "Apr 3, 2026" — medium date, no time */
export function formatDateMedium(value: DateInput, fallback = ''): string {
  return safeDayjsFormat(value, 'MMM D, YYYY', { fallback });
}

/** "04/03/2026, 10:33 AM" — compact with time */
export function formatDateTimeCompact(value: DateInput, fallback = ''): string {
  return safeDayjsFormat(value, 'MM/DD/YYYY, hh:mm A', { fallback });
}

/** "Apr 3, 2026, 10:33:09 AM" — with seconds, optional locale for i18n */
export function formatDateTimeWithSeconds(
  value: DateInput,
  fallback = '',
  locale?: string,
): string {
  return safeDayjsFormat(value, 'MMM D, YYYY, hh:mm:ss A', { fallback, locale });
}

/** "2026-04-03 10:33:09" — machine-readable for CSV export */
export function formatDateForCsv(value: DateInput, fallback = ''): string {
  return safeDayjsFormat(value, 'YYYY-MM-DD HH:mm:ss', { fallback });
}

/** Format with any dayjs pattern — for SmallApp manifest format strings.
 *  Pass `tz` (e.g. 'America/Chicago') to convert before formatting.
 *  Pass `locale` (e.g. 'ja') for localized month/day names. */
export function formatWithPattern(
  value: DateInput,
  pattern: string,
  fallback = '',
  opts?: { tz?: string; locale?: string },
): string {
  return safeDayjsFormat(value, pattern, { fallback, ...opts });
}
